import { Box, Container, Flex, Spinner, Stack, Tabs, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { BotGoneBanner } from '#features/discord-lfg-admin/components/BotGoneBanner';
import { GuildHeader } from '#features/discord-lfg-admin/components/GuildHeader';
import { GuildHelp } from '#features/discord-lfg-admin/components/GuildHelp';
import { GuildOverview } from '#features/discord-lfg-admin/components/GuildOverview';
import { LfgSettingsTable } from '#features/discord-lfg-admin/components/LfgSettingsTable';
import { SignInWithDiscord } from '#features/discord-lfg-admin/components/SignInWithDiscord';
import {
  isBotNotInGuildError,
  isDiscordReauthRequiredError,
  isGuildForbiddenError,
  useGuildDetails,
} from '#features/discord-lfg-admin/hooks/useGuildDetails';
import { landingContrastCss } from '#features/discord-lfg-admin/styles';
import { useGames } from '#features/game';

export const Route = createFileRoute('/{-$locale}/_layout/discord-lfg-bot/admin/$guildId')({
  ssr: false,
  component: DiscordLfgAdminGuildPage,
  // Private admin dashboard — keep it out of search indexes.
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
});

function DiscordLfgAdminGuildPage() {
  const { t } = useLingui();
  const { guildId } = Route.useParams();
  const navigate = useNavigate();
  const isAuth = useIsAuth();
  const { data, isLoading, error } = useGuildDetails(guildId);
  const games = useGames();
  const [tab, setTab] = useState('overview');

  // Available games for the AddNotification modal — gateway/discord backend
  // accepts any game id, but the LFG bot only fires for `session: true` games,
  // so we filter to those for clarity. `useGames()` returns a union with
  // GlobalError, so we narrow via `__typename === 'Game'` (matches existing
  // usage in features/posts/Filters).
  const availableGames = useMemo(() => {
    if (!games) return [];
    return games
      .filter(
        (g): g is Extract<NonNullable<typeof g>, { __typename: 'Game' }> =>
          g != null && g.__typename === 'Game'
      )
      .filter((g) => g.session !== false)
      .map((g) => ({
        id: g.id,
        name: g.name,
        icon: g.icon,
        slug: g.slug ?? undefined,
      }));
  }, [games]);

  // FORBIDDEN: the user is no longer admin / lost Manage Server permission.
  // Bounce them back to the guild list so they pick a server they actually own.
  useEffect(() => {
    if (isGuildForbiddenError(error)) {
      navigate({ to: '/{-$locale}/discord-lfg-bot/admin' });
    }
  }, [error, navigate]);

  if (!isAuth) {
    return (
      <Box css={landingContrastCss}>
        <SignInWithDiscord variant="signIn" />
      </Box>
    );
  }

  if (isDiscordReauthRequiredError(error)) {
    return (
      <Box css={landingContrastCss}>
        <SignInWithDiscord variant="connectScopes" />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Container maxW="4xl" py={8}>
        <Flex justify="center" py={12}>
          <Spinner />
        </Flex>
      </Container>
    );
  }

  if (isBotNotInGuildError(error)) {
    return (
      <Container maxW="4xl" py={8}>
        <BotGoneBanner guildId={guildId} botClientId={data?.discordLfgAdmin?.botClientId} />
      </Container>
    );
  }

  if (isGuildForbiddenError(error)) {
    // useEffect above navigates; render nothing in the interim.
    return null;
  }

  if (error) {
    return (
      <Container maxW="4xl" py={8}>
        <Box py={8} textAlign="center" color="fg.error">
          <Text>
            {error instanceof Error ? error.message : t`Failed to load this Discord server.`}
          </Text>
        </Box>
      </Container>
    );
  }

  const details = data?.discordLfgAdmin?.guild;
  if (!details) {
    return (
      <Container maxW="4xl" py={8}>
        <Box py={8} textAlign="center" color="fg.muted">
          <Text>{t`Server not found.`}</Text>
        </Box>
      </Container>
    );
  }

  const summary = details.summary;
  const textChannels = (details.textChannels ?? []).filter(
    (c): c is NonNullable<typeof c> => c != null
  );
  const lfgSettings = (details.lfgSettings ?? []).filter(
    (s): s is NonNullable<typeof s> => s != null
  );
  const cardStats = details.cardStats ?? null;
  const deliveredByChannel = cardStats
    ? Object.fromEntries(cardStats.byChannel.map((c) => [c.channelId, c.count]))
    : undefined;

  return (
    <Container maxW="4xl" py={8} css={landingContrastCss}>
      <Stack gap={6}>
        <GuildHeader guild={summary} />
        {!summary.botInstalled && (
          <BotGoneBanner guildId={guildId} botClientId={data?.discordLfgAdmin?.botClientId} />
        )}
        <Tabs.Root value={tab} onValueChange={(e) => setTab(e.value)} colorPalette="purple">
          <Tabs.List>
            <Tabs.Trigger value="overview">{t`Overview`}</Tabs.Trigger>
            <Tabs.Trigger value="notifications">{t`Notifications`}</Tabs.Trigger>
            <Tabs.Trigger value="help">{t`Help`}</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="overview" pt={6}>
            <GuildOverview
              summary={summary}
              settings={lfgSettings}
              cardStats={cardStats}
              onManageNotifications={() => setTab('notifications')}
            />
          </Tabs.Content>
          <Tabs.Content value="notifications" pt={6}>
            <LfgSettingsTable
              guildId={guildId}
              settings={lfgSettings}
              textChannels={textChannels}
              availableGames={availableGames}
              deliveredByChannel={deliveredByChannel}
            />
          </Tabs.Content>
          <Tabs.Content value="help" pt={6}>
            <GuildHelp />
          </Tabs.Content>
        </Tabs.Root>
      </Stack>
    </Container>
  );
}
